import { SubscriptionPlanOption } from '@/services/provider/subscription-plan-options.service';

export interface NormalizedPlanOptionsStore {
  plansById: Record<string, SubscriptionPlanOption>;
  rootPlans: SubscriptionPlanOption[];
  variantsByParentId: Record<string, SubscriptionPlanOption[]>;
  currentPlanId: string | null;
}

export function normalizePlanOptions(
  options: SubscriptionPlanOption[],
  fallbackCurrentPlanId?: string,
): NormalizedPlanOptionsStore {
  const plansById: Record<string, SubscriptionPlanOption> = {};
  const variantsByParentId: Record<string, SubscriptionPlanOption[]> = {};
  const rootPlans: SubscriptionPlanOption[] = [];

  let currentPlanId: string | null = null;

  options.forEach((plan) => {
    if (!plan.id) return;

    plansById[plan.id] = plan;

    if (plan.isCurrentPlan && !currentPlanId) {
      currentPlanId = plan.id;
    }

    if (plan.parentPlanId && plan.parentPlanId !== plan.id) {
      if (!variantsByParentId[plan.parentPlanId]) {
        variantsByParentId[plan.parentPlanId] = [];
      }
      variantsByParentId[plan.parentPlanId].push(plan);
      return;
    }

    rootPlans.push(plan);
  });

  Object.entries(variantsByParentId).forEach(([parentId, variants]) => {
    if (!plansById[parentId]) {
      rootPlans.push(...variants);
      delete variantsByParentId[parentId];
    }
  });

  return {
    plansById,
    rootPlans,
    variantsByParentId,
    currentPlanId: currentPlanId ?? fallbackCurrentPlanId ?? null,
  };
}

export function selectIsWorkspaceRequired(
  store: NormalizedPlanOptionsStore,
  planId: string | undefined,
  gateEnabled: boolean,
): boolean {
  if (!planId || !gateEnabled) return false;
  return Boolean(store.plansById[planId]?.requiresWorkspaceNameForSwitch);
}

export function selectAvailableCycles(
  store: NormalizedPlanOptionsStore,
  planId: string | undefined,
) {
  if (!planId) return [];
  return store.plansById[planId]?.pricing ?? [];
}

export function selectIsCurrentPlan(
  store: NormalizedPlanOptionsStore,
  planId: string | undefined,
): boolean {
  if (!planId) return false;
  const plan = store.plansById[planId];
  return Boolean(plan?.isCurrentPlan || store.currentPlanId === planId);
}

export function validateWorkspaceName(workspaceName: string): string | null {
  const trimmed = workspaceName.trim();

  if (!trimmed) {
    return 'required';
  }

  if (trimmed.length < 2) {
    return 'min';
  }

  if (trimmed.length > 255) {
    return 'max';
  }

  return null;
}
